import api from './api';
import { ApiResponse } from '../types';
import {
  CANONICAL_PROFESSIONAL_TYPES,
  PROFESSIONAL_TYPE_LABELS,
} from '../types/professionalRates';

export interface Conversation {
  id: string;
  onderwerp: string;
  type: 'professional_employer' | 'community' | 'admin_broadcast' | 'professional_peer' | 'professional_group' | 'professional_belagom' | 'opleider_professional' | 'opleider_belagom' | 'opleider_employer' | 'opleider_groep' | 'vp_melding';
  is_active: boolean;
  is_public?: boolean;
  is_broadcast?: boolean;
  group_name?: string;
  group_avatar_path?: string;
  laatste_bericht?: string;
  last_message?: string;
  last_message_at?: string;
  created_at: string;
  last_read_at?: string;
  is_muted?: boolean;
  unread_count?: number;
  participant?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    profile_photo?: string;
  };
}

export interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string;
  type: 'text' | 'file';
  file_url?: string;
  file_name?: string;
  created_at: string;
  first_name?: string;
  last_name?: string;
  email?: string;
}

export interface ConversationParticipant {
  user_id: string;
  conversation_id: string;
  last_read_at?: string;
  is_muted: boolean;
}

export interface ProfessionalInDirectory {
  id: string;
  first_name: string;
  last_name: string;
  profile_photo_path?: string;
  professional_type: string;
  professional_type_label: string;
  active_regions?: string[];
  city?: string | null;
  is_online: boolean;
  last_seen?: string;
}

export const PROFESSIONAL_TYPES = CANONICAL_PROFESSIONAL_TYPES.map(value => ({
  value,
  label: PROFESSIONAL_TYPE_LABELS[value],
}));

export const PROVINCES = [
  'Groningen', 'Friesland', 'Drenthe', 'Overijssel',
  'Flevoland', 'Gelderland', 'Utrecht', 'Noord-Holland',
  'Zuid-Holland', 'Zeeland', 'Noord-Brabant', 'Limburg',
];

export const chatAPI = {
  // Get all conversations for current user
  getConversations: async (): Promise<Conversation[]> => {
    const response = await api.get<ApiResponse<Conversation[]>>('/communication/conversations');
    return response.data.data || [];
  },

  // Get or create conversation between professional and employer
  getOrCreateProfessionalEmployerConversation: async (employerId?: string, professionalId?: string): Promise<Conversation> => {
    const response = await api.post<ApiResponse<Conversation>>(
      '/communication/conversations/professional-employer',
      { employer_id: employerId, professional_id: professionalId }
    );
    return response.data.data!;
  },

  // Get messages for a conversation
  getMessages: async (conversationId: string): Promise<Message[]> => {
    const response = await api.get<ApiResponse<Message[]>>(
      `/communication/conversations/${conversationId}/messages`
    );
    return response.data.data || [];
  },

  // Send a message
  sendMessage: async (
    conversationId: string,
    content: string,
    type: 'text' | 'file' = 'text'
  ): Promise<Message> => {
    const response = await api.post<ApiResponse<Message>>('/communication/messages', {
      conversation_id: conversationId,
      content,
      type
    });
    return response.data.data!;
  },

  // Create community chat
  createCommunityChat: async (
    communityTopic: string,
    isPublic: boolean = true
  ): Promise<Conversation> => {
    const response = await api.post<ApiResponse<Conversation>>(
      '/communication/conversations/community',
      {
        community_topic: communityTopic,
        is_public: isPublic
      }
    );
    return response.data.data!;
  },

  // Search professionals in directory
  searchProfessionals: async (params: {
    name?: string;
    province?: string;
    professional_type?: string;
  }): Promise<ProfessionalInDirectory[]> => {
    const query = new URLSearchParams();
    if (params.name) query.set('name', params.name);
    if (params.province) query.set('province', params.province);
    if (params.professional_type) query.set('professional_type', params.professional_type);
    const response = await api.get<ApiResponse<ProfessionalInDirectory[]>>(
      `/communication/professionals?${query.toString()}`
    );
    return response.data.data || [];
  },

  // Get or create 1-on-1 peer conversation between professionals
  getOrCreatePeerConversation: async (otherProfessionalId: string): Promise<Conversation> => {
    const response = await api.post<ApiResponse<Conversation>>(
      '/communication/conversations/professional-peer',
      { other_professional_id: otherProfessionalId }
    );
    return response.data.data!;
  },

  // Get opleider contacts (registered professionals + Belagom admins)
  getOpleiderContacts: async (): Promise<{ professionals: any[]; admins: any[] }> => {
    const response = await api.get('/communication/opleider-contacts');
    return response.data.data || { professionals: [], admins: [] };
  },

  // Get or create opleider conversation (with professional or Belagom)
  getOrCreateOpleiderConversation: async (
    otherUserId: string,
    type: 'opleider_professional' | 'opleider_belagom' | 'opleider_employer'
  ): Promise<Conversation> => {
    const response = await api.post<ApiResponse<Conversation>>(
      '/communication/conversations/opleider-contact',
      { other_user_id: otherUserId, type }
    );
    return response.data.data!;
  },

  // Create a group conversation
  createGroupConversation: async (groupName: string, participantIds: string[]): Promise<Conversation> => {
    const response = await api.post<ApiResponse<Conversation>>(
      '/communication/conversations/professional-group',
      { group_name: groupName, participant_ids: participantIds }
    );
    return response.data.data!;
  },

  // Add a participant to a group conversation
  addParticipantToGroup: async (conversationId: string, userId: string): Promise<void> => {
    await api.post(`/communication/conversations/${conversationId}/participants`, { user_id: userId });
  },

  // Get or create klantenservice conversation (professional → Belagom)
  getOrCreateBelagomSupportConversation: async (): Promise<Conversation> => {
    const response = await api.post<ApiResponse<Conversation>>(
      '/communication/conversations/professional-belagom'
    );
    return response.data.data!;
  },

  // Admin: get all klantenservice conversations
  getAdminSupportConversations: async (): Promise<any[]> => {
    const response = await api.get('/admin/support-conversations');
    return response.data.data || [];
  },
};

